import React, { useCallback, useMemo, useState } from "react";
import moment from "moment";
import { useHistory, useLocation } from "react-router-dom";
import { useQuery } from "react-query";
import { FORM_ERROR } from "final-form";
import {
  mutateErrors,
  normalizePhoneNumber,
  removeRedundantKeys,
  urlReplacer,
} from "../../../utils";
import { User } from "../../user/types";
import { useApi } from "../../../hooks/useApi";
import useUserQueries from "../../user/hooks/useUserQueries";
import { ApiRoutes } from "../../../constants/apiRoutes";
import { objectToFormData } from "../../../utils/objectToFormData";
import { UserRole } from "../../user/enums/UserRole";
import { NotifyMode } from "../../notify/NotifyModeEnum";
import { useNotify } from "../../notify/useNotify";
import { Routes } from "../../../constants/routes";
import { useRecaptcha } from "../../../hooks/useRecaptcha";
import { DATE_FORMATS } from "../../../constants";

export function useAuthProvider() {
  const api = useApi();
  const location = useLocation();
  const history = useHistory();
  const { fetchMyAccountProfile } = useUserQueries();
  const snackNotify = useNotify();
  const { executeRecaptcha } = useRecaptcha();

  const [user, setUser] = useState<User | null>(null);
  const [authorizing, setAuthorizing] = React.useState<boolean>(true);
  const [verifying, setVerifying] = React.useState<boolean>(false);

  const profile = useQuery<any>(
    `profile_${user && user.id}`,
    fetchMyAccountProfile,
    {
      enabled:
        user &&
        [
          UserRole.Agent,
          UserRole.Parent,
          UserRole.School,
          UserRole.Student,
          UserRole.ServiceProvider,
          UserRole.Teacher,
        ].includes(user.role),
    },
  );

  const isAuthenticated = useMemo(() => user !== null, [user]);

  const logout = React.useCallback((skipRedirectUrl = false) => {
    setUser(null);
    localStorage.removeItem("token");

    if (skipRedirectUrl) {
      history.push(`/${Routes.LOGIN}`);
    }
  }, []);

  const login = React.useCallback(
    async (data) => {
      try {
        const recaptchaToken = await executeRecaptcha("login");

        let {
          data: { user },
        } = await api.request({
          data,
          method: "POST",
          url: ApiRoutes.LOGIN,
          recaptchaToken,
        });

        setAuthUser(user);
      } catch (e) {
        let { message } = mutateErrors(e);

        return { [FORM_ERROR]: message };
      }
    },
    [executeRecaptcha],
  ); // eslint-disable-line react-hooks/exhaustive-deps

  const register = React.useCallback(
    async (data, role) => {
      let recaptchaToken;
      try {
        let url;
        let certificateFiles: any[] = [];
        let cvFile: any = null;
        switch (role) {
          case UserRole.Agent:
            recaptchaToken = await executeRecaptcha("register_agent");

            data = {
              ...data,
              name: data.name.trim(),
              email: data.email.trim(),
              image: data.image?.file,
              agentProfile: {
                ...data.agentProfile,
                name: data.name.trim(),
                country: data.agentProfile.country.code,
                website: data.agentProfile?.website?.trim(),
                title: data.agentProfile.title.trim(),
              },
            };

            delete data.agentProfile.country.name;

            url = ApiRoutes.REGISTER_AGENT;
            break;
          case UserRole.School:
            recaptchaToken = await executeRecaptcha("register_school");
            data = {
              ...data,
              name: data.name.trim(),
              image: data.image.file,
              email: data.email.trim(),
              schoolProfile: {
                ...data.schoolProfile,
                school: {
                  ...data.schoolProfile.school,
                  name: data.schoolProfile.school.name.trim(),
                  country: data.schoolProfile.school.country.code,
                  typeId: data.schoolProfile.school.type.id,
                  website: data.schoolProfile.school.website.trim(),
                },
              },
            };

            delete data.schoolProfile.school.type;

            url = ApiRoutes.REGISTER_SCHOOL;
            break;
          case UserRole.ServiceProvider:
            recaptchaToken = await executeRecaptcha(
              "register_service_provider",
            );
            data = {
              ...data,
              name: data.name.trim(),
              image: data.image.file,
              email: data.email.trim(),
              serviceProviderProfile: {
                ...data.serviceProviderProfile,
                serviceProvider: {
                  ...data.serviceProviderProfile.serviceProvider,
                  name: data.serviceProviderProfile.serviceProvider.name.trim(),
                  country:
                    data.serviceProviderProfile.serviceProvider.country.code,
                  website:
                    data.serviceProviderProfile.serviceProvider.website.trim(),
                },
              },
            };

            delete data.serviceProviderProfile.serviceProvider.type;

            url = ApiRoutes.REGISTER_SERVICE_PROVIDER;
            break;
          case UserRole.Student:
            recaptchaToken = await executeRecaptcha("register_student");
            data = {
              ...data,
              name: data.name.trim(),
              email: data.email.trim(),
              image: data.image?.file,
              studentProfile: {
                ...data.studentProfile,
                birth_date: moment(data.studentProfile.birth_date).format(
                  DATE_FORMATS.frontEnd,
                ),
                country: data.studentProfile.country.code,
                citizenship: data.studentProfile.citizenship.code,
                city: data.studentProfile.city.trim(),
                gender: data.studentProfile.gender.id,
              },
            };
            url = ApiRoutes.REGISTER_STUDENT;
            break;
          case UserRole.Parent:
            recaptchaToken = await executeRecaptcha("register_parent");
            data = {
              ...data,
              name: data.name.trim(),
              email: data.email.trim(),
              image: data.image?.file,
            };
            url = ApiRoutes.REGISTER_PARENT;
            break;
          case UserRole.Teacher:
            recaptchaToken = await executeRecaptcha("register_teacher");
            const details = Object.entries(data.teacherProfile.details).map(
              ([key, content]) => {
                const type = key.split("_")[1];

                return {
                  type,
                  content,
                };
              },
            );
            
            certificateFiles = data.certificates?.map((c: any) => c.file) || [];
            cvFile = data.cv?.file;

            data = {
              ...data,
              name: data.name.trim(),
              email: data.email.trim(),
              image: data.image?.file,
              teacherProfile: {
                ...data.teacherProfile,
                country: data.teacherProfile.country.code,
                details: JSON.stringify(details),
              },
            };
            delete data.certificates;
            delete data.cv;
            url = ApiRoutes.REGISTER_TEACHER;
            break;
        }

        data = removeRedundantKeys(data);

        const formData = objectToFormData({
          ...data,
          phone: normalizePhoneNumber(data.phone),
        });

        if (certificateFiles?.length) {
          certificateFiles.forEach((file: any) => {
            formData.append("certificates", file);
          });
        }
        if (cvFile) {
          formData.append("cv", cvFile);
        }

        const response = await api.request({
          method: "POST",
          data: formData,
          url,
          recaptchaToken,
        });

        setAuthUser(response.data.user);
      } catch (e) {
        const { errors, message } = mutateErrors(e);
        snackNotify("Error", message, NotifyMode.ERROR);
        return {
          ...errors,
        };
      }
    },
    [executeRecaptcha],
  ); // eslint-disable-line react-hooks/exhaustive-deps

  const setAuthUser = useCallback((user: User) => {
    localStorage.setItem("token", user.accessToken.token);
    setUser(user);
  }, []);

  const resetPassword = React.useCallback(async (data: string) => {
    try {
      const url = new URLSearchParams(location.search);
      const code = url.get("code");
      await api.request({
        data,
        method: "POST",
        url: urlReplacer(ApiRoutes.RESET_PASSWORD, {
          code,
        }),
      });
    } catch (e) {
      const { errors } = mutateErrors(e);
      return { ...errors };
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const authorize = React.useCallback(
    async (token?: string | null) => {
      setAuthorizing(true);
      if (!isAuthenticated) {
        token = token || localStorage.getItem("token");
        if (token) {
          try {
            let {
              data: { user },
            } = await api.request({
              method: "get",
              url: "auth/me",
              accessToken: token,
            });
            setAuthUser(user);
          } catch (e) {
            logout();
          }
        } else {
          logout();
        }
      }
      setAuthorizing(false);
    },
    [api],
  ); // eslint-disable-line react-hooks/exhaustive-deps

  const refetch = useCallback(async () => {
    try {
      let {
        data: { user: respUser },
      } = await api.request({
        method: "get",
        url: "auth/me",
        accessToken: user?.accessToken.token,
      });
      setAuthUser(respUser);
    } catch (e) {
      logout();
    }
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const sendVerificationEmail = React.useCallback(async (email) => {
    setVerifying(true);
    try {
      await api.request({
        method: "POST",
        data: { email },
        url: ApiRoutes.VERIFY,
      });
      setVerifying(false);
    } catch (e) {
      setVerifying(false);

      const { errors } = mutateErrors(e);
      return { ...errors };
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const sendPasswordResetEmail = React.useCallback(async ({ email }) => {
    try {
      await api.request({
        method: "POST",
        url: ApiRoutes.FORGOT_PASSWORD,
        data: { email },
      });
    } catch (e) {
      const { errors } = mutateErrors(e);
      return { ...errors };
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const setPhoto = React.useCallback((photo) => {
    // @ts-ignore
    setUser((prevState) => ({
      ...prevState,
      photo,
    }));
  }, []);

  return {
    user,
    profile,
    logout,
    data: {
      verifying,
      authorizing: authorizing || profile.isLoading || profile.isFetching,
      isAuthenticated,
    },
    actions: {
      login,
      register,
      authorize,
      refetch,
      resetPassword,
      sendVerificationEmail,
      sendPasswordResetEmail,
      setPhoto,
    },
  };
}
